import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { FeedbackItem, FeedbackStatus } from "../types";

const CATEGORY_DOT: Record<string, string> = {
  bug: "#D46A5A",
  idea: "#C89B3C",
  question: "#3B82C4",
};

const STATUS_PILL: Record<FeedbackStatus, { cls: string; label: string }> = {
  new: { cls: "bg-warning/15 text-warning", label: "New" },
  replied: { cls: "bg-accent-soft text-accent-deep", label: "Replied" },
  resolved: { cls: "bg-success/15 text-success", label: "Resolved" },
};

function FilterPill({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors duration-150 cursor-pointer border ${
        active
          ? "border-warning bg-warning/15 text-warning"
          : "border-border text-text-tertiary hover:text-ink bg-transparent"
      }`}
    >
      {label}
    </button>
  );
}

function FeedbackCard({ item, onUpdated }: { item: FeedbackItem; onUpdated: (fresh: FeedbackItem) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dot = CATEGORY_DOT[item.category] ?? "#D46A5A";
  const pill = STATUS_PILL[item.status] ?? STATUS_PILL.new;

  const sendReply = async () => {
    if (!reply.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const fresh = await api.replyFeedback(item.id, reply.trim());
      // reply endpoint doesn't return heard_about; keep the one we have
      onUpdated({ ...fresh, heard_about: item.heard_about });
      setReply("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send reply.");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status: FeedbackStatus) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const fresh = await api.updateFeedbackStatus(item.id, status);
      // status endpoint doesn't return user_email / heard_about; keep what we have
      onUpdated({ ...fresh, user_email: item.user_email, heard_about: item.heard_about });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update status.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card px-5 py-4">
      <button
        type="button"
        className="w-full text-left cursor-pointer bg-transparent border-none p-0"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center justify-between gap-3 mb-1.5 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dot }} />
            <span className="text-[0.65rem] uppercase tracking-[0.12em] font-semibold text-text-tertiary">
              {item.category}
            </span>
            <span className="text-sm text-ink font-medium truncate">{item.user_email ?? "deleted user"}</span>
            {item.plan && (
              <span className="text-[0.6rem] uppercase tracking-wide px-2 py-0.5 rounded-full bg-surface2 text-text-tertiary font-semibold shrink-0">
                {item.plan}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2.5 shrink-0">
            <span className={`text-[0.6rem] uppercase tracking-[0.1em] py-0.5 px-2 rounded-full font-semibold ${pill.cls}`}>
              {pill.label}
            </span>
            <span className="text-xs text-text-tertiary">{new Date(item.created_at).toLocaleString()}</span>
          </div>
        </div>
        <p className={`text-sm text-text-secondary leading-relaxed whitespace-pre-wrap ${expanded ? "" : "line-clamp-2"}`}>
          {item.message}
        </p>
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-border-soft">
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-text-tertiary mb-3">
            {item.route && <span>Route: <span className="text-text-secondary">{item.route}</span></span>}
            {item.session_id != null && <span>Session: <span className="text-text-secondary">#{item.session_id}</span></span>}
            {item.heard_about && <span>Heard about us: <span className="text-text-secondary">{item.heard_about}</span></span>}
            {item.user_agent && <span className="break-all">UA: <span className="text-text-secondary">{item.user_agent}</span></span>}
          </div>

          {item.reply_text && (
            <div className="bg-surface border border-border-soft rounded-[10px] px-4 py-3 mb-3">
              <div className="text-[0.65rem] uppercase tracking-[0.12em] font-semibold text-text-tertiary mb-1">
                Your reply {item.replied_at ? `· ${new Date(item.replied_at).toLocaleString()}` : ""}
              </div>
              <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-wrap">{item.reply_text}</p>
            </div>
          )}

          <textarea
            className="input-base w-full resize-none focus:outline-none text-base sm:text-sm"
            rows={3}
            placeholder={item.reply_text ? "Send a follow-up email…" : "Write a reply — it's emailed to the user…"}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          {error && <p className="text-error text-xs mt-2">{error}</p>}
          <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
            <div className="flex gap-2">
              {item.status !== "resolved" ? (
                <button className="btn-quiet text-xs px-4 py-2" onClick={() => setStatus("resolved")} disabled={busy}>
                  Mark resolved
                </button>
              ) : (
                <button className="btn-quiet text-xs px-4 py-2" onClick={() => setStatus("new")} disabled={busy}>
                  Reopen
                </button>
              )}
            </div>
            <button
              className="btn-primary text-xs px-5 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={sendReply}
              disabled={!reply.trim() || busy}
            >
              {busy ? "Sending…" : "Send reply"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminFeedbackPage() {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [items, setItems] = useState<FeedbackItem[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  useEffect(() => {
    api.getMe()
      .then((me) => {
        if (!me.is_admin) {
          navigate("/", { replace: true });
          return;
        }
        setAllowed(true);
      })
      .catch(() => navigate("/", { replace: true }));
  }, [navigate]);

  useEffect(() => {
    if (!allowed) return;
    api.getFeedback().then(setItems).catch(console.error);
  }, [allowed]);

  if (!allowed) return null;

  const filtered = (items ?? []).filter(
    (i) => (statusFilter ? i.status === statusFilter : true) && (categoryFilter ? i.category === categoryFilter : true),
  );
  const newCount = (items ?? []).filter((i) => i.status === "new").length;

  const onUpdated = (fresh: FeedbackItem) =>
    setItems((prev) => (prev ? prev.map((i) => (i.id === fresh.id ? fresh : i)) : prev));

  return (
    <div className="max-w-4xl mx-auto w-full px-5 sm:px-8 py-10">
      <header className="mb-6 animate-rise">
        <h1>Feedback inbox</h1>
        <p className="text-text-tertiary text-[0.9375rem] mt-1">
          {items === null
            ? "Loading…"
            : newCount > 0
            ? `${newCount} new ${newCount === 1 ? "submission" : "submissions"} waiting.`
            : "All caught up."}
        </p>
      </header>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          <FilterPill active={statusFilter === null} label="All" onClick={() => setStatusFilter(null)} />
          {(["new", "replied", "resolved"] as FeedbackStatus[]).map((s) => (
            <FilterPill key={s} active={statusFilter === s} label={STATUS_PILL[s].label} onClick={() => setStatusFilter((p) => (p === s ? null : s))} />
          ))}
          <span className="w-px h-4 bg-border mx-1.5" />
          {["bug", "idea", "question"].map((c) => (
            <FilterPill key={c} active={categoryFilter === c} label={c[0].toUpperCase() + c.slice(1)} onClick={() => setCategoryFilter((p) => (p === c ? null : c))} />
          ))}
        </div>

        {items !== null && filtered.length === 0 ? (
          <p className="text-text-tertiary italic text-sm">
            {items.length === 0 ? "No feedback yet." : "Nothing matches these filters."}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {filtered.map((item) => (
              <FeedbackCard key={item.id} item={item} onUpdated={onUpdated} />
            ))}
          </div>
        )}
    </div>
  );
}
